import { capitalize } from "es-toolkit";
import type { CSSProperties } from "react";

import type { NumberOperator } from "@skilitics/judy";

import { isTernaryOperator, operatorToEnglish } from "../../effects-conditions/index.ts";

export type ResponsiveConditionConfig = {
  id: number;
  dimension: "width" | "height";
  minimum: CSSProperties["width"] | CSSProperties["height"];
  maximum: CSSProperties["width"] | CSSProperties["height"];
  operator: "" | NumberOperator;
};

const getCondition = (condition?: ResponsiveConditionConfig) => {
  if (!(condition && condition.operator && condition.dimension)) return;

  const { dimension, operator, minimum, maximum } = condition;

  if (operator === ("between" as NumberOperator)) {
    return `(${dimension} >= ${minimum}) and (${dimension} <= ${maximum})`;
  }

  if (operator === ("!between" as NumberOperator)) {
    return `(${dimension} < ${minimum}) or (${dimension} > ${maximum})`;
  }

  return `${dimension} ${operator} ${minimum}`;
};

export const getResponsiveCss = (responsiveConditions: ResponsiveConditionConfig[]) => {
  const responsiveRules = responsiveConditions.map(getCondition).filter(Boolean);

  return responsiveRules.map(
    // NOTE: it's important to keep this as a raw string as it is passed around config and publish.
    // It will be parsed as CSS with emotion css() on render.
    (rule) => `
      @container composable-canvas-container (${rule}) {
        display: none;
      }
    `,
  );
};

export const getResponsiveConditionLabel = (condition: ResponsiveConditionConfig) => {
  if (!(condition && condition.operator && condition.dimension)) return;

  const operator = operatorToEnglish(condition.operator);
  const isTernary = isTernaryOperator(condition.operator);

  // TODO: multilingual
  return `${capitalize(condition.dimension)} ${operator} ${condition.minimum}${isTernary ? ` and ${condition.maximum}` : ""}`;
};
